import React from 'react';
import { Button, Typography, Space, Alert, Input, Collapse } from 'antd';
import { FeatureCardsArray, Feature } from '../FeatureCard/FeatureCard';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

interface FeatureSelectionProps {
    features: { [category: string]: Feature[] }; // Now expects backend keys
    carDetails: any;
    additionalRequirements: string;
    onAdditionalRequirementsChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onStatusChange: (newStatus: 'added' | 'pending', category: string, featureText: string) => void; // featureText is now just text
    onBack: () => void;
    onGenerate: () => void;
    reportStatus: string;
    onViewReport: () => void;
}

export const FeatureSelection: React.FC<FeatureSelectionProps> = ({
    features,
    carDetails,
    additionalRequirements,
    onAdditionalRequirementsChange,
    onStatusChange,
    onBack,
    onGenerate,
    reportStatus,
    onViewReport
}) => {
    const vehicleName = `${carDetails.Year} ${carDetails.Make} ${carDetails.Model} ${carDetails.Trims ? carDetails.Trims.join(' ') : ''}`.trim();

    // Separate features for special rendering
    const { trimPackages, interiorFeatures, ...otherFeatures } = features;
    return (
        <div>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4}>Customize Your Research</Title>
                <Space>
                    <Button onClick={onBack}>Back</Button>
                    {reportStatus === 'completed' && (
                        <Button type="primary" onClick={onViewReport}>View Existing Report</Button>
                    )}
                </Space>
            </div>

            <Alert
                message={
                    <span style={{ fontSize: 16 }}>
                        We found available features for the <strong>{vehicleName}</strong>.
                        Select the features that match your desired configuration using the <strong>+</strong> buttons.
                        We will generate a specific market report based on your exact preferences.
                    </span>
                }
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
            />

            <div style={{ marginBottom: 40 }}>
                {trimPackages && (
                    <FeatureCardsArray
                        initialFeatures={{ trimPackages }}
                        handleStatusChange={onStatusChange}
                    />
                )}
                <FeatureCardsArray
                    initialFeatures={otherFeatures}
                    handleStatusChange={onStatusChange}
                />
                {interiorFeatures && (
                    <Collapse bordered={false} style={{ background: 'white' }} className="feature-accordion">
                        <Panel header={<h2 style={{ margin: 0 }}>Interior Features</h2>} key="1">
                            <FeatureCardsArray
                                initialFeatures={{ interiorFeatures }}
                                handleStatusChange={onStatusChange}
                                showTitle={false}
                            />
                        </Panel>
                    </Collapse>
                )}
            </div>

            <div style={{ marginTop: 40 }}>
                <Title level={4}>Additional Requirements</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    Add any other must-haves or specific details you're looking for.
                </Text>
                <TextArea
                    rows={4}
                    value={additionalRequirements}
                    onChange={onAdditionalRequirementsChange}
                    placeholder="ie: specific sports packages, exterior accessories, etc."
                />
            </div>

            <div style={{ textAlign: 'center', marginTop: 40, marginBottom: 40 }}>
                <Button type="primary" onClick={onGenerate} size="large" style={{ minWidth: 200 }}>
                    {reportStatus === 'completed' ? 'Rerun Research Report' : 'Generate Research Report'}
                </Button>
            </div>

            <div style={{ marginTop: 40, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={onBack}>Back</Button>
                {reportStatus === 'completed' && (
                    <Button type="primary" onClick={onViewReport}>View Existing Report</Button>
                )}
            </div>
        </div>
    );
};
